// Story 12.4 — Section embedded in the bot detail page that surfaces
// Evolution Go connection state and actions. Renders nothing for bots with
// `provider: cloud-api` (or no provider — defaults to cloud-api).
//
// Story 13.x cleanup — el flujo Evolution Go tiene 2 modos distintos:
//   A) Operador trae credenciales de una instancia ya creada y conectada
//      en SU PROPIO Evolution Go server (caso típico SaaS). Aquí el QR
//      ya se escaneó externamente; el framework solo necesita pollear
//      el estado y refrescar.
//   B) Operador delega la creación de la instancia al framework (Evolution
//      Go alojado por nosotros). Aquí el QR sí se escanea desde la UI.
// El componente AHORA expone ambas opciones explícitamente: "Refrescar
// estado" como primary cuando el operador maneja externamente, y
// "Conectar via QR" como secondary/avanzado.
//
// Story 17-2 — el estado sale de `state` (vinculado es `loggedIn`, no
// `connected`), el error del server se muestra tal cual (p. ej. la licencia
// sin activar) y el panel lleva el aviso de uso de Evolution GO.

import { Link2, Loader2, Power, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useEvolutionDisconnect } from '@/hooks/mutations/use-evolution-connect';
import {
  type EvolutionLinkState,
  linkStateOf,
  useEvolutionStatus,
} from '@/hooks/queries/use-evolution-status';
import { EvolutionConnectModal } from './EvolutionConnectModal';
import { EvolutionGoNotice } from './EvolutionGoNotice';
import { shouldShowEvolutionPanel } from './should-show-panel';

interface Props {
  botId: number;
  botName: string;
  botConfig: Record<string, unknown> | null | undefined;
}

export function EvolutionConnectionPanel({
  botId,
  botName,
  botConfig,
}: Props): React.ReactElement | null {
  // Code review P1 — hooks MUST be called unconditionally and in the same
  // order on every render (React Rules of Hooks). The provider gate runs
  // after all hook calls; only the JSX is conditional.
  const isEvolutionBot = shouldShowEvolutionPanel(botConfig);
  const status = useEvolutionStatus(botId, { enabled: isEvolutionBot });
  const disconnect = useEvolutionDisconnect(botId);
  const [modalOpen, setModalOpen] = useState(false);

  if (!isEvolutionBot) return null;

  const state = linkStateOf(status.data);
  const isConnected = state === 'linked';
  // Con sesión (vinculado o reconectando) se puede cerrar; sin ella, vincular.
  const hasSession = state === 'linked' || state === 'reconnecting';

  function handleDisconnect(): void {
    if (
      !confirm(
        `¿Cerrar la sesión de WhatsApp en "${botName}"? La instancia requerirá un nuevo QR para reconectar.`,
      )
    ) {
      return;
    }
    disconnect.mutate();
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Conexión WhatsApp (Evolution GO)</h3>
            <StatusBadge
              loading={status.isLoading}
              failed={status.isError}
              state={state}
              jid={status.data?.jid}
            />
          </div>
        </CardHeader>
        <CardContent>
          {status.error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <p className="font-semibold">No se pudo consultar el estado</p>
              <p className="mt-1 text-xs">{(status.error as Error).message}</p>
            </div>
          ) : isConnected && status.data ? (
            <p className="text-sm text-muted-foreground">
              Conectado{status.data.jid ? ' como ' : ''}
              {status.data.jid && <span className="font-mono">{status.data.jid}</span>}
              {status.data.name ? ` (${status.data.name})` : ''}
            </p>
          ) : state === 'reconnecting' ? (
            <p className="text-sm text-muted-foreground">
              El número sigue vinculado, pero Evolution GO no tiene conexión con WhatsApp en este
              momento. Se reconecta solo; si no vuelve, revisa el servidor.
            </p>
          ) : state === 'qr-pending' ? (
            <p className="text-sm text-muted-foreground">
              Evolution GO está esperando que se escanee el QR. Ábrelo con <em>Conectar via QR</em>.
            </p>
          ) : (
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                <strong>La instancia no responde como conectada.</strong> Esto puede deberse a uno
                de dos casos:
              </p>
              <ul className="list-disc space-y-1 pl-5">
                <li>
                  Tu instancia se administra <strong>fuera del framework</strong> (panel propio de
                  Evolution Go). Verifica el estado allí y luego presiona <em>Refrescar</em>.
                </li>
                <li>
                  La instancia <strong>se creó dentro del framework</strong> y necesita un primer
                  pairing. Usa <em>Conectar via QR</em>.
                </li>
              </ul>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => status.refetch()} disabled={status.isFetching}>
              {status.isFetching ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-1 h-4 w-4" />
              )}
              Refrescar estado
            </Button>
            {!hasSession && (
              <Button
                variant="ghost"
                onClick={() => setModalOpen(true)}
                disabled={status.isLoading}
              >
                <Link2 className="mr-1 h-4 w-4" />
                Conectar via QR
              </Button>
            )}
            {hasSession && (
              <Button variant="ghost" onClick={handleDisconnect} disabled={disconnect.isPending}>
                {disconnect.isPending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Power className="mr-1 h-4 w-4" />
                )}
                Desconectar
              </Button>
            )}
          </div>
          <div className="mt-4 border-t border-border pt-3">
            <EvolutionGoNotice />
          </div>
        </CardContent>
      </Card>

      <EvolutionConnectModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        botId={botId}
        botName={botName}
      />
    </>
  );
}

const BADGES: Record<EvolutionLinkState, { label: string; tone: 'ok' | 'warn' }> = {
  linked: { label: 'Conectado', tone: 'ok' },
  reconnecting: { label: 'Reconectando', tone: 'warn' },
  'qr-pending': { label: 'QR pendiente', tone: 'warn' },
  disconnected: { label: 'Desconectado', tone: 'warn' },
};

function StatusBadge({
  loading,
  failed,
  state,
  jid,
}: {
  loading: boolean;
  failed: boolean;
  state: EvolutionLinkState;
  jid?: string;
}): React.ReactElement {
  if (loading) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-xs">
        <Loader2 className="h-3 w-3 animate-spin" />
        Cargando
      </span>
    );
  }
  if (failed) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
        <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden />
        Sin respuesta
      </span>
    );
  }
  const { label, tone } = BADGES[state];
  if (tone === 'ok') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-300">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
        {label}
        {jid ? <span className="font-mono opacity-70">· {jid.slice(0, 13)}</span> : null}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-300">
      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
      {label}
    </span>
  );
}
